// Body tree registry. Maps body id → fully-formed BodyTree.
// Bodies with hand-authored trees are imported directly. Everything else
// falls through to the stub generator (driven by celestialData).

import { sunTree } from './sun.js';
import { mercuryTree } from './mercury.js';
import { venusTree } from './venus.js';
import { earthTree } from './earth.js';
import { marsTree } from './mars.js';
import { jupiterTree } from './jupiter.js';
import { saturnTree } from './saturn.js';
import { uranusTree } from './uranus.js';
import { neptuneTree } from './neptune.js';
import { plutoTree } from './pluto.js';
import { monolithTree } from './monolith.js';
import { wormholeTree } from './wormhole.js';
import { buildStubTree } from './_stub.js';
import { listSystems } from '../../systems/index.js';
import { MOON_TREES } from './moons.js';
import { SMALL_BODY_TREES } from './smallbodies.js';
import { DEEP_SKY_TREES } from './deepsky.js';

const handAuthored = {
  sun: sunTree,
  mercury: mercuryTree,
  venus: venusTree,
  earth: earthTree,
  mars: marsTree,
  jupiter: jupiterTree,
  saturn: saturnTree,
  uranus: uranusTree,
  neptune: neptuneTree,
  pluto: plutoTree,
  monolith: monolithTree,
  wormhole: wormholeTree
};

// Moons, small bodies and spacecraft, and the deep sky, one module each.
const authoredGroups = [MOON_TREES, SMALL_BODY_TREES, DEEP_SKY_TREES];

export function getBody(bodyId) {
  if (!bodyId) return null;
  const id = bodyId.toLowerCase();
  if (handAuthored[id]) return handAuthored[id];
  for (const group of authoredGroups) if (group[id]) return group[id];
  const stub = buildStubTree(id);
  if (stub) return stub;
  // Fall through to the multi-system catalog: Alpha Centauri stars +
  // planets, Gargantua + its Interstellar worlds. These aren't in
  // celestialData (the legacy Sol-only store) but ship the same
  // display fields the stub tree needs, so we build a parallel tree
  // here so the matryoshka panel can render them.
  return buildRemoteSystemTree(id);
}

function buildRemoteSystemTree(id) {
  for (const sys of listSystems()) {
    if (sys.legacy) continue;
    const star = sys.stars && sys.stars.find(s => s.id === id);
    if (star) return remoteStarTree(star, sys);
    const planet = sys.planets && sys.planets.find(p => p.id === id);
    if (planet) return remotePlanetTree(planet, sys);
  }
  return null;
}

function remoteStarTree(star, sys) {
  return {
    id: star.id,
    label: star.name,
    type: star.type === 'BlackHole' ? 'Black hole' : `Star (${star.spectralClass || 'unknown class'})`,
    summary: star.description,
    payload: [
      { type: 'description', text: star.description },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Spectral class', value: star.spectralClass },
          { label: 'Diameter', value: star.realDiameter },
          { label: 'Surface temperature', value: star.temperature ? `${star.temperature.toLocaleString()} K` : null },
          { label: 'Host system', value: sys.name },
          { label: 'Distance from Sol', value: `${sys.distanceLY.toLocaleString()} ly` }
        ].filter(e => e.value)
      }
    ],
    children: []
  };
}

function remotePlanetTree(planet, sys) {
  const star = sys.stars.find(s => s.id === planet.hostStarId);
  return {
    id: planet.id,
    label: planet.name,
    type: planet.type,
    summary: planet.description,
    payload: [
      { type: 'description', text: planet.description },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Type', value: planet.type },
          { label: 'Diameter', value: planet.realDiameter },
          { label: 'Distance from host', value: planet.realDistance },
          { label: 'Orbital period', value: planet.orbitalPeriod },
          { label: 'Host star', value: star ? star.name : planet.hostStarId },
          { label: 'Host system', value: sys.name }
        ].filter(e => e.value)
      }
    ],
    children: []
  };
}

// Resolve a path like ['earth', 'atmosphere', 'composition'] → the deepest
// node found, plus the actual array of nodes traversed (so the caller can
// build a breadcrumb). Returns null if the root id doesn't resolve.
export function resolvePath(path) {
  if (!path || !path.length) return null;
  const root = getBody(path[0]);
  if (!root) return null;

  const trail = [root];
  let current = root;
  for (let i = 1; i < path.length; i++) {
    const segment = path[i];
    const next = (current.children || []).find(c => c.id === segment);
    if (!next) break;
    current = next;
    trail.push(current);
  }
  return { node: current, trail };
}
